import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaLink,
  FaExternalLinkAlt,
  FaPalette,
  FaMousePointer,
  FaSitemap,
  FaExclamationTriangle,
  FaHome,
  FaGlobe,
  FaFolder
} from 'react-icons/fa';

const HTMLLesson14 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- Basic link syntax -->
<a href="https://codeflare.tech">Visit Our Website</a>

<!-- Link with title attribute -->
<a 
  href="https://codeflare.tech" 
  title="Visit CodeFlare's Official Website"
>
  CodeFlare Tech
</a>

<!-- Link opening in new tab -->
<a 
  href="https://codeflare.tech" 
  target="_blank"
>
  Open in New Tab
</a>`;

  const code2 = `<!-- Absolute URL (external) -->
<a href="https://www.codeflarelimited.com/">
  External Website
</a>

<!-- Relative URL (same website) -->
<a href="/about">About Page</a>
<a href="contact.html">Contact Page</a>

<!-- Relative URL to subfolder -->
<a href="/products/software">Software Products</a>
<a href="../docs/manual.pdf">Download Manual</a>

<!-- Anchor link (same page) -->
<a href="#section1">Jump to Section 1</a>
<a href="#top">Back to Top</a>`;

  const code3 = `<!-- Default link states -->
<style>
  /* Unvisited link */
  a:link {
    color: blue;
    text-decoration: underline;
  }
  
  /* Visited link */
  a:visited {
    color: purple;
  }
  
  /* Mouse over link */
  a:hover {
    color: red;
    text-decoration: none;
  }
  
  /* Selected link */
  a:active {
    color: green;
  }
</style>

<!-- Custom styled links -->
<a href="/home" class="btn-link">Home</a>
<a href="/about" class="nav-link">About</a>

<style>
.btn-link {
  background: #3b82f6;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  text-decoration: none;
}

.nav-link {
  color: #1e293b;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
}

.nav-link:hover {
  border-bottom-color: #3b82f6;
  color: #3b82f6;
}
</style>`;

  const code4 = `<!-- Open in new window/tab -->
<a href="https://example.com" target="_blank">
  External Site (New Tab)
</a>

<!-- Open in same tab (default) -->
<a href="/page2" target="_self">
  Next Page (Same Tab)
</a>

<!-- Open in parent frame -->
<a href="/help" target="_parent">
  Help (Parent Frame)
</a>

<!-- Open in full window body -->
<a href="/full" target="_top">
  Full Window
</a>

<!-- Open in specific iframe -->
<a href="/widget" target="myFrame">
  Open in Frame
</a>

<!-- Security best practice for external links -->
<a 
  href="https://external-site.com" 
  target="_blank" 
  rel="noopener noreferrer"
>
  Secure External Link
</a>`;

  const code5 = `<!-- Email link -->
<a href="mailto:contact@codeflare.tech">
  Email Us
</a>

<!-- Email with subject and body -->
<a href="mailto:contact@codeflare.tech?subject=Website%20Inquiry&body=Hello%20Team">
  Contact with Pre-filled Email
</a>

<!-- Phone link -->
<a href="tel:+1234567890">
  Call Us: +1 (234) 567-890
</a>

<!-- SMS link -->
<a href="sms:+1234567890?body=Hello%20I%20have%20a%20question">
  Send SMS
</a>

<!-- Download link -->
<a href="/files/document.pdf" download>
  Download PDF
</a>

<!-- Download with custom filename -->
<a href="/files/report.docx" download="Q4-Report.docx">
  Download Quarterly Report
</a>`;

  const code6 = `<!DOCTYPE html>
<html>
<head>
  <style>
    /* Link styling system */
    :root {
      --primary: #3b82f6;
      --secondary: #64748b;
      --success: #10b981;
      --danger: #ef4444;
    }
    
    .link-demo {
      max-width: 800px;
      margin: 2rem auto;
      padding: 2rem;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    
    .link-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      margin: 2rem 0;
    }
    
    .link-card {
      padding: 1.5rem;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      text-align: center;
      transition: all 0.3s ease;
    }
    
    .link-card:hover {
      border-color: var(--primary);
      transform: translateY(-2px);
    }
    
    /* Different link styles */
    .link-primary {
      color: var(--primary);
      text-decoration: none;
      font-weight: 600;
      border-bottom: 2px solid transparent;
      transition: border-color 0.3s ease;
    }
    
    .link-primary:hover {
      border-bottom-color: var(--primary);
    }
    
    .link-button {
      display: inline-block;
      background: var(--primary);
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      text-decoration: none;
      font-weight: 600;
      transition: background 0.3s ease;
    }
    
    .link-button:hover {
      background: #2563eb;
    }
    
    .link-secondary {
      background: #f8fafc;
      color: var(--secondary);
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      text-decoration: none;
      border: 1px solid #e2e8f0;
      transition: all 0.3s ease;
    }
    
    .link-secondary:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
    
    /* Navigation example */
    .nav-menu {
      display: flex;
      gap: 2rem;
      padding: 1rem;
      background: #f8fafc;
      border-radius: 8px;
      margin: 2rem 0;
    }
    
    .nav-link {
      color: #64748b;
      text-decoration: none;
      font-weight: 500;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      transition: all 0.3s ease;
    }
    
    .nav-link:hover,
    .nav-link.active {
      color: var(--primary);
      background: white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  </style>
</head>
<body>
  <div class="link-demo">
    <h1>HTML Links Implementation Guide</h1>
    
    <!-- Navigation Menu -->
    <nav class="nav-menu">
      <a href="#home" class="nav-link active">Home</a>
      <a href="#about" class="nav-link">About</a>
      <a href="#services" class="nav-link">Services</a>
      <a href="#contact" class="nav-link">Contact</a>
    </nav>
    
    <!-- Different Link Types -->
    <section>
      <h2>Link Types and Examples</h2>
      <div class="link-grid">
        <div class="link-card">
          <h3>External Links</h3>
          <a href="https://codeflare.tech" target="_blank" rel="noopener noreferrer" class="link-button">
            Visit CodeFlare
          </a>
          <p>Opens in new tab securely</p>
        </div>
        
        <div class="link-card">
          <h3>Internal Links</h3>
          <a href="/about" class="link-primary">
            About Our Company
          </a>
          <p>Navigation within site</p>
        </div>
        
        <div class="link-card">
          <h3>Email Links</h3>
          <a href="mailto:contact@codeflare.tech" class="link-secondary">
            Contact Us
          </a>
          <p>Opens email client</p>
        </div>
        
        <div class="link-card">
          <h3>Download Links</h3>
          <a href="/brochure.pdf" download class="link-button">
            Download Brochure
          </a>
          <p>Forces file download</p>
        </div>
      </div>
    </section>
    
    <!-- Link States Demonstration -->
    <section style="margin-top: 3rem;">
      <h2>Link States and Interactions</h2>
      <div style="display: flex; gap: 2rem; flex-wrap: wrap; margin: 1rem 0;">
        <a href="#unvisited" class="link-primary">Unvisited Link</a>
        <a href="#visited" style="color: purple;">Visited Link</a>
        <a href="#hover" class="link-primary" style="background: #fef3c7;">Hover State</a>
        <a href="#active" style="color: green; font-weight: bold;">Active Link</a>
      </div>
    </section>
    
    <!-- Advanced Link Features -->
    <section style="margin-top: 3rem;">
      <h2>Advanced Link Features</h2>
      <div style="background: #f8fafc; padding: 1.5rem; border-radius: 8px;">
        <p>
          <a href="tel:+15551234567">📞 Call: +1 (555) 123-4567</a>
        </p>
        <p>
          <a href="sms:+15551234567?body=Hello%20I'm%20interested%20in%20your%20services">
            💬 Send us a Text Message
          </a>
        </p>
        <p>
          <a href="#main-content" class="link-secondary">
            ⬆️ Skip to Main Content (Accessibility)
          </a>
        </p>
      </div>
    </section>
  </div>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Links Introduction",
      content: "Hyperlinks are the foundation of web navigation, connecting documents and resources across the internet",
      keyPoints: [
        "Links created with <a> anchor tag",
        "href attribute specifies destination",
        "Can link to pages, files, emails, and phone numbers",
        "Fundamental for website navigation and user experience"
      ],
      icon: <FaLink className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Absolute vs Relative URLs", 
      content: "Understand the difference between absolute URLs for external resources and relative URLs for internal navigation",
      keyPoints: [
        "Absolute URLs: Full web addresses (https://...)",
        "Relative URLs: Paths relative to current page",
        "Root-relative: Starts with / (from site root)",
        "Document-relative: Path from current location"
      ],
      icon: <FaGlobe className="section-icon" />,
      color: "#059669",
      code: code2
    },
    {
      title: "Link Styling and Colors",
      content: "Customize link appearance using CSS with different states for user interaction and visual feedback",
      keyPoints: [
        "Four link states: link, visited, hover, active",
        "Default colors: blue (unvisited), purple (visited)",
        "Use CSS pseudo-classes for state styling",
        "Important for usability and accessibility"
      ],
      icon: <FaPalette className="section-icon" />,
      color: "#dc2626",
      code: code3
    },
    {
      title: "Target Attribute and Security",
      content: "Control where links open using the target attribute and implement security best practices for external links",
      keyPoints: [
        "target='_blank' opens in new tab/window",
        "target='_self' opens in same tab (default)",
        "Use rel='noopener noreferrer' for security",
        "Prevents tabnabbing and performance issues"
      ],
      icon: <FaExternalLinkAlt className="section-icon" />,
      color: "#7c3aed",
      code: code4
    },
    {
      title: "Special Link Types",
      content: "Beyond web pages, HTML links can trigger emails, phone calls, SMS messages, and file downloads",
      keyPoints: [
        "mailto: links open email client",
        "tel: links trigger phone calls on mobile",
        "sms: links open messaging app",
        "download attribute forces file download"
      ],
      icon: <FaMousePointer className="section-icon" />,
      color: "#d97706",
      code: code5
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 14 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Links Mastery</h1>
              <p className="html-lesson-subtitle">
                Master web navigation with comprehensive link implementation, styling, and best practices
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Link Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Link Implementation Power
              </div>
              <h3 className="html-visual-title">
                From Basic Navigation to Advanced Interactive Links
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>Link Syntax Foundation</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<!-- Basic link structure -->
<a href="destination">Link Text</a>

<!-- Common attributes -->
<a 
  href="url"
  target="_blank"
  title="Tooltip text"
  rel="noopener"
>
  Enhanced Link
</a>`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Live Link Examples</h4>
                <div className="html-link-preview">
                  <div className="link-demo-item">
                    <a href="#basic" className="basic-link">Basic Navigation Link</a>
                    <span>Standard text link</span>
                  </div>
                  <div className="link-demo-item">
                    <a href="#button" className="button-link">Button Style Link</a>
                    <span>Styled as button</span>
                  </div>
                  <div className="link-demo-item">
                    <a href="#external" className="external-link" target="_blank">
                      External Link <FaExternalLinkAlt size={12} />
                    </a>
                    <span>Opens in new tab</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Remember:</strong> Always use descriptive link text for accessibility - avoid "click here" or "read more".</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Target Attribute Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaExternalLinkAlt className="html-badge-icon" />
                Target Attribute Guide
              </div>
              <h3 className="html-comparison-title">
                Where Your Links Open - Target Attribute Values
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Target Value</th>
                    <th>Behavior</th>
                    <th>Use Case</th>
                    <th>Security Consideration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>_blank</code></td>
                    <td>Opens in new window/tab</td>
                    <td>External websites, references</td>
                    <td>Use <code>rel="noopener noreferrer"</code></td>
                  </tr>
                  <tr>
                    <td><code>_self</code></td>
                    <td>Opens in same frame (default)</td>
                    <td>Internal navigation, same site</td>
                    <td>Safe for internal links</td>
                  </tr>
                  <tr>
                    <td><code>_parent</code></td>
                    <td>Opens in parent frame</td>
                    <td>Framed websites, nested content</td>
                    <td>Rarely used in modern web</td>
                  </tr>
                  <tr>
                    <td><code>_top</code></td>
                    <td>Opens in full window body</td>
                    <td>Breaking out of frames</td>
                    <td>Frame-busting technique</td>
                  </tr>
                  <tr>
                    <td><code>framename</code></td>
                    <td>Opens in named frame</td>
                    <td>Specific frame targeting</td>
                    <td>Legacy frames usage</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Link Best Practices */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Link Best Practices
              </div>
              <h3 className="html-practices-title">
                Professional Link Implementation Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do These</h4>
                <ul>
                  <li>Use descriptive, meaningful link text</li>
                  <li>Add rel="noopener noreferrer" for target="_blank"</li>
                  <li>Ensure color contrast meets accessibility standards</li>
                  <li>Provide visual feedback on hover/focus</li>
                  <li>Use title attribute for additional context</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li>Using "click here" or "read more" as link text</li>
                  <li>Opening internal links in new tabs unnecessarily</li>
                  <li>Making links too small to click easily</li>
                  <li>Using only color to distinguish links</li>
                  <li>Broken links or incorrect URLs</li>
                </ul>
              </div>
            </div>
          </div>

          {/* URL Types Guide */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaSitemap className="html-badge-icon" />
                URL Path Types
              </div>
              <h3 className="html-multiple-title">
                Understanding Absolute vs Relative URLs
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-explanation">
                <h4>URL Path Types Explained:</h4>
                <ul>
                  <li><strong>Absolute URLs:</strong> Complete web address
                    <ul>
                      <li><code>https://www.example.com/page.html</code></li>
                      <li>Use for external websites</li>
                      <li>Always works from any location</li>
                    </ul>
                  </li>
                  <li><strong>Root-relative URLs:</strong> From site root
                    <ul>
                      <li><code>/images/photo.jpg</code></li>
                      <li>Starts with forward slash</li>
                      <li>Works from any page on same domain</li>
                    </ul>
                  </li>
                  <li><strong>Document-relative URLs:</strong> From current file
                    <ul>
                      <li><code>../parent/page.html</code> (parent directory)</li>
                      <li><code>images/icon.png</code> (subdirectory)</li>
                      <li><code>#section</code> (same page anchor)</li>
                    </ul>
                  </li>
                </ul>
              </div>
              
              <div className="html-url-examples">
                <h4>URL Examples in Context:</h4>
                <div className="url-demo">
                  <div className="url-item">
                    <div className="url-type">Absolute</div>
                    <code>https://codeflare.tech/products</code>
                  </div>
                  <div className="url-item">
                    <div className="url-type">Root-relative</div>
                    <code>/assets/styles.css</code>
                  </div>
                  <div className="url-item">
                    <div className="url-type">Document-relative</div>
                    <code>../../docs/manual.pdf</code>
                  </div>
                  <div className="url-item">
                    <div className="url-type">Anchor</div>
                    <code>#contact-form</code>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional Link System Implementation
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code6}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Link Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML links?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned comprehensive link implementation including syntax, URL types, styling, target attributes, security best practices, and special link types. Start creating intuitive web navigation!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-quote" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson14;